/**
 * RecurringEventEditModal Component
 * 
 * This component displays a modal dialog when a user attempts to edit or delete a recurring event.
 * It provides three options for handling recurring events:
 * 1. "This event" - Modify only the selected instance
 * 2. "This and following events" - Modify this instance and all future occurrences
 * 3. "All events" - Modify all instances in the series
 * 
 * The component is designed to work for both edit and delete operations with appropriate UI changes.
 * 
 * @param {Object} props - Component properties
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when the modal is closed
 * @param {Function} props.onEditChoice - Callback when user makes a choice, receives: 'this', 'following', or 'all'
 * @param {boolean} props.isDelete - Whether this is a delete operation (changes UI text accordingly)
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const RecurringEventEditModal = ({ isOpen, onClose, onEditChoice, isDelete = false }) => {
  const [selectedOption, setSelectedOption] = useState('this');
  
  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOption('this');
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  const handleSubmit = () => {
    onEditChoice(selectedOption);
  };
  
  const title = isDelete ? 'Delete Recurring Event' : 'Edit Recurring Event';
  const actionText = isDelete ? 'delete' : 'edit';
  const buttonText = isDelete ? 'Delete' : 'Continue';
  const buttonColor = isDelete ? '#ef4444' : '#4285F4';
  const buttonHoverColor = isDelete ? '#dc2626' : '#3367d6';
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        width: '90%',
        maxWidth: '450px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      }}>
        <h2 style={{ 
          marginTop: '0', 
          marginBottom: '20px',
          fontSize: '20px',
          color: '#2d3748',
          fontWeight: '700'
        }}>
          {title}
        </h2>
        
        <p style={{ fontSize: '15px', color: '#4a5568', marginBottom: '20px' }}>
          Would you like to {actionText}:
        </p>
        
        <div style={{ marginBottom: '20px' }}>
          {/* Option for "This event only" */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: selectedOption === 'this' ? '#f0f9ff' : 'transparent',
            marginBottom: '8px',
            cursor: 'pointer',
            border: selectedOption === 'this' ? '1px solid #93c5fd' : '1px solid #e2e8f0',
          }} 
          onClick={() => setSelectedOption('this')}>
            <input 
              type="radio" 
              id="this-event" 
              name="edit-choice" 
              value="this" 
              checked={selectedOption === 'this'} 
              onChange={() => setSelectedOption('this')}
              style={{ marginRight: '12px' }}
            />
            <div>
              <label htmlFor="this-event" style={{ 
                display: 'block', 
                fontWeight: '600',
                fontSize: '15px',
                color: '#2d3748',
                cursor: 'pointer' 
              }}>
                This event
              </label>
              <span style={{ fontSize: '13px', color: '#718096' }}>
                Only this instance will be {actionText}d
              </span>
            </div>
          </div>
          
          {/* Option for "This and following events" */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: selectedOption === 'following' ? '#f0f9ff' : 'transparent',
            marginBottom: '8px',
            cursor: 'pointer',
            border: selectedOption === 'following' ? '1px solid #93c5fd' : '1px solid #e2e8f0',
          }} 
          onClick={() => setSelectedOption('following')}>
            <input 
              type="radio" 
              id="following-events" 
              name="edit-choice" 
              value="following" 
              checked={selectedOption === 'following'} 
              onChange={() => setSelectedOption('following')}
              style={{ marginRight: '12px' }}
            />
            <div>
              <label htmlFor="following-events" style={{ 
                display: 'block', 
                fontWeight: '600',
                fontSize: '15px',
                color: '#2d3748',
                cursor: 'pointer' 
              }}>
                This and following events
              </label>
              <span style={{ fontSize: '13px', color: '#718096' }}>
                This and all future instances will be {actionText}d
              </span>
            </div>
          </div>
          
          {/* Option for "All events" */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: selectedOption === 'all' ? '#f0f9ff' : 'transparent',
            cursor: 'pointer',
            border: selectedOption === 'all' ? '1px solid #93c5fd' : '1px solid #e2e8f0',
          }} 
          onClick={() => setSelectedOption('all')}>
            <input 
              type="radio" 
              id="all-events" 
              name="edit-choice" 
              value="all" 
              checked={selectedOption === 'all'} 
              onChange={() => setSelectedOption('all')}
              style={{ marginRight: '12px' }}
            />
            <div>
              <label htmlFor="all-events" style={{ 
                display: 'block', 
                fontWeight: '600',
                fontSize: '15px',
                color: '#2d3748',
                cursor: 'pointer' 
              }}>
                All events
              </label>
              <span style={{ fontSize: '13px', color: '#718096' }}>
                All instances of this recurring event will be {actionText}d
              </span>
            </div>
          </div>
        </div>
        
        {/* Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '24px'
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#e5e7eb',
              color: '#4b5563',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#d1d5db';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#e5e7eb';
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            style={{
              padding: '10px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: buttonColor,
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = buttonHoverColor;
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = buttonColor;
            }}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

RecurringEventEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEditChoice: PropTypes.func.isRequired,
  isDelete: PropTypes.bool
};

export default RecurringEventEditModal;
